import React, { useEffect, useState, useMemo } from "react";
import MainTrends from "../components/MainTrends";
import { MoveDown, MoveUp } from "lucide-react";
import SubjectWisePerformance from "../components/SubjectWisePerformance";
import ActionableFeedback from "../components/ActionableFeedback";
import { useLocation, useParams } from "react-router-dom";
import { getProfileTrackingData } from "../../../services/profileTrackingService";
import {
  addActionableFeedback,
  getActionableFeedback,
  getSyllabusProgress,
} from "../../../services/syllabusProgress";
import { getImageFromAssetFolder } from "../../../utils/assets";
import PerformanceMetrics from "../components/PerformanceMetrics";
import ActionableFeedbackDialog from "../components/ActionableFeedbackDialog";
import { getUser } from "../../../utils/auth";
import { toast } from "react-toastify";
import ModalLayout from "../../../components/ModalLayout";
import SyllabusHierarchyViewer from "../components/WholisticView";
import { getUserDashboard } from "../../../services/dashboardService";
import OverviewSection from "../components/OverviewSection";
import PerformanceTrendSection from "../components/PerformanceTrendSection";
import SignalsSection from "../components/SignalsSection";
import SubjectPerformanceSection from "../components/SubjectPerformanceSection";
import SpeedVsAccuracySection from "../components/SpeedVsAccuracySection";
import QuestionTypeAnalysisSection from "../components/QuestionTypeAnalysisSection";
import DifficultyAnalysisSection from "../components/DifficultyAnalysisSection";
import RecommendationsFeedbacksSection from "../components/RecommendationsFeedbacksSection";
import LearningMomentumSection from "../components/LearningMomentumSection";

const UserReport = () => {
  const { userId, syllabusId, sessionId } = useParams();
  const location = useLocation();
  const [user] = useState(location.state);

  const [dashboardData, setDashboardData] = useState({});
  const [syllabusProgress, setSyllabusProgress] = useState({});
  const [actionableFeedback, setActionableFeedback] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddFeedback, setAddFeedbackOpen] = useState(false);
  const [showWholeView, setShowWholeView] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const promises = await Promise.all([
          getUserDashboard(userId, syllabusId, sessionId),
          getSyllabusProgress(userId, syllabusId),
          getActionableFeedback(userId),
        ]);

        setDashboardData(promises[0].data);
        setSyllabusProgress(promises[1].data);
        setActionableFeedback(promises[2].data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId && syllabusId) {
      fetchProfile();
    }
  }, [userId, syllabusId]);

  const saveFeedback = async (feedback) => {
    const mentor = getUser();
    feedback.submittedBy = { name: mentor.name, mentorId: mentor.id };
    feedback.userId = user.id;
    const res = await addActionableFeedback([feedback]);
    if (res.status == "success") {
      setActionableFeedback([...actionableFeedback, ...res.data]);
      toast.success("Feedback added!");
    } else {
      toast.error("Something went wrong while adding feedback!");
    }
  };

  if (loading)
    return (
      <div className="p-6 text-center text-gray-500 font-medium">
        Loading users...
      </div>
    );
  if (error)
    return (
      <div className="p-6 text-center text-red-500 font-medium">
        Error: {error}
      </div>
    );

  return (
    <>
      <div className="flex-1 p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8 bg-primaryLighter min-h-screen">
        {/* ========================= */}
        {/* USER HEADER + STATS       */}
        {/* ========================= */}
        <section className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 lg:border-none lg:bg-transparent lg:p-0 lg:shadow-none">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div
              className="flex items-center gap-4 cursor-pointer hover:opacity-90 transition group min-w-0"
              onClick={() => setShowWholeView(true)}
            >
              <img
                className="h-12 w-12 rounded-full object-cover ring-2 ring-primary/10 group-hover:ring-primary/30 transition flex-shrink-0"
                src={user.photoUrl || getImageFromAssetFolder("short_logo.png")}
                alt={user.name}
              />
              <div className="min-w-0">
                <p className="m-0 font-bold text-primary text-base md:text-lg truncate">
                  {user.name}
                </p>
                <p className="m-0 text-gray-500 text-sm truncate">
                  {syllabusProgress?.syllabus?.name || "No syllabus assigned"}
                </p>
              </div>
            </div>

            <div className="w-full lg:w-auto flex-grow">
              <OverviewSection
                overviewData={dashboardData.overview}
                syllabusProgress={syllabusProgress.progress?.progress?.summary}
                sessionId={sessionId}
                userId={userId}
                syllabusId={syllabusId}
              />
            </div>
          </div>
        </section>

        {/* ========================= */}
        {/* MAIN CONTENT WORKSPACE    */}
        {/* ========================= */}
        <section className="flex flex-col xl:flex-row gap-6 items-start">
          {/* ANALYSIS PLOTS - LEFT VIEW */}
          <div className="w-full xl:w-3/4 space-y-6">
            <PerformanceTrendSection
              performanceData={dashboardData.performanceTrend}
            />

            <SignalsSection signalsData={dashboardData.signals} />

            <SubjectPerformanceSection
              subjectData={dashboardData.subjectPerformance}
            />

            <SpeedVsAccuracySection
              speedAccuracyData={dashboardData.speedVsAccuracy}
            />

            <QuestionTypeAnalysisSection
              questionTypeData={dashboardData.questionTypeAnalysis}
            />

            <DifficultyAnalysisSection
              difficultyData={dashboardData.difficultyAnalysis}
            />

            <LearningMomentumSection
              learningMomentumData={dashboardData.learningMomentum}
            />
          </div>

          {/* RECOMMENDATIONS & FEEDBACKS PANELS - RIGHT VIEW */}
          <div className="w-full xl:w-1/4 xl:sticky xl:top-6">
            <RecommendationsFeedbacksSection
              feedbacks={dashboardData.feedbacks}
              recommendations={dashboardData.recommendations}
            />
          </div>
        </section>
      </div>

      {/* OVERLAY DIALOG MODALS */}
      <ActionableFeedbackDialog
        isOpen={openAddFeedback}
        onClose={() => setAddFeedbackOpen(false)}
        onSubmit={saveFeedback}
      />

      {showWholeView && syllabusProgress && (
        <ModalLayout closeHandler={() => setShowWholeView(false)} size="large">
          <div className="w-full max-h-[80vh] overflow-y-auto overflow-x-auto p-2">
            <SyllabusHierarchyViewer progress={syllabusProgress} />
          </div>
        </ModalLayout>
      )}
    </>
  );
};

export default UserReport;
